import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, Subject } from 'rxjs';
import { switchMap, takeUntil, tap } from 'rxjs/operators';

import { InvitationAccessLevel, InvitationStatusType } from '@vi-iot/client';

import {
  InstallationDetailsStore,
  InvitationsStore,
  InstallationRoleManagementInvitation,
} from '@store';

import { RoleManagementModalComponent } from './role-management-modal/role-management-modal.component';
import { TranslateService } from '@ngx-translate/core';

export type InstallationRoleManagementRole = 'Operator' | 'ServiceContractor' | 'Installer';

@Component({
  selector: 'app-building-manager-card',
  templateUrl: './building-manager-card.component.html',
  styleUrls: ['./building-manager-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BuildingManagerCardComponent implements OnInit, OnDestroy {
  InvitationAccessLevel = InvitationAccessLevel;
  InvitationStatusType = InvitationStatusType;
  installationId: number;
  invitationsLoaded$: Observable<boolean>;

  private roleManagementInvitations: InstallationRoleManagementInvitation[];

  private ngUnsubscribe: Subject<void> = new Subject<void>();

  constructor(
    public installationDetailsStore: InstallationDetailsStore,
    private invitationsStore: InvitationsStore,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.invitationsLoaded$ = this.invitationsStore.invitationsLoaded$;
    this.getInstallationRoleManagementInvitations();
  }

  openRoleManagementModal(): void {
    this.dialog.open(RoleManagementModalComponent, {
      width: '457px',
      panelClass: 'role-management-modal',
      data: {
        installationId: this.installationId,
      },
      autoFocus: false,
      disableClose: false,
    });
  }

  getInstallationRoleManagementInvitations(): void {
    this.installationDetailsStore.getInstallationId$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        tap((id) => {
          this.installationId = id;
          this.invitationsStore.listInstallationInvitations(this.installationId);
        }),
        switchMap((id) => this.invitationsStore.getInstallationRoleManagementInvitations(id))
      )
      .subscribe((invitations) => {
        this.roleManagementInvitations = invitations.map((entry) => ({
          email: entry.inviteeEmail,
          status: entry.status,
          accessLevel: entry.accessLevel,
          invitationToken: entry.token,
        }));
        this.cdr.markForCheck();
      });
  }

  getRoleAssigneeEmail(role: InstallationRoleManagementRole): string {
    return this.roleManagementInvitations.find((entry) => entry.accessLevel === role)?.email;
  }

  getIsRoleAssigneeOtherThanAccepted(role: InstallationRoleManagementRole): string | null {
    const invitation = this.roleManagementInvitations.find((entry) => entry.accessLevel === role);

    if (invitation?.status !== InvitationStatusType.Accepted) {
      if (invitation?.status === InvitationStatusType.Pending) {
        return this.translateService.instant(
          '+INSTALLATIONS.INSTALLATION.DEVICE_CARD.SHARE_DEVICE_MONITORING.INVITATION_PENDING'
        );
      } else if (invitation?.status === InvitationStatusType.Canceled) {
        return this.translateService.instant(
          '+INSTALLATIONS.INSTALLATION.DEVICE_CARD.SHARE_DEVICE_MONITORING.INVITATION_CANCELED'
        );
      } else if (invitation?.status === InvitationStatusType.Rejected) {
        return this.translateService.instant(
          '+INSTALLATIONS.INSTALLATION.DEVICE_CARD.SHARE_DEVICE_MONITORING.INVITATION_REJECTED'
        );
      } else if (invitation?.status === InvitationStatusType.Terminated) {
        return this.translateService.instant(
          '+INSTALLATIONS.INSTALLATION.DEVICE_CARD.SHARE_DEVICE_MONITORING.INVITATION_TERMINATED'
        );
      } else if (invitation?.status === InvitationStatusType.Expired) {
        return this.translateService.instant(
          '+INSTALLATIONS.INSTALLATION.DEVICE_CARD.SHARE_DEVICE_MONITORING.INVITATION_EXPIRED'
        );
      }
    } else return null;
  }

  getRoleAssigneeStatus(role: InstallationRoleManagementRole): InvitationStatusType {
    return this.roleManagementInvitations.find((entry) => entry.accessLevel === role)?.status;
  }
  getRoleAssigneeCompanyName(role: InvitationAccessLevel): Observable<string> {
    return this.installationDetailsStore.getAccessListCompanyName(role);
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
